import { EventReward, parseReward } from "./events";
import { OfferPrice, priceCents } from "./offers";

/**
 * The season pass, "War Campaign" (MP-22), parsed from the generated bundle's `seasons` section
 * (balance/src/seasons.json; gen.py checks every rule and adds `epochDay`). The rules that read it are meta/season.ts.
 */

/** Where pass XP comes from (seasons.json `xp`). */
export const PASS_XP_SOURCES = ["win", "loss", "order", "eventNode", "dailyRoad"] as const;
export type PassXpSource = (typeof PASS_XP_SOURCES)[number];

/** A reward on a track: the tier that opens it and what it pays (the event reward keys). */
export interface PassTierReward {
  readonly tier: number;
  readonly reward: EventReward;
  /** The premium tier that also pays the season's cosmetic (its skin, else its title). */
  readonly cosmetic: boolean;
}

/** One season's name and cosmetics; the list repeats after its last entry. */
export interface SeasonTheme {
  readonly name: string;
  /** A season skin id (shop.json skins), "" while its art does not exist. */
  readonly skin: string;
  /** The commander title Premium earns on the cosmetic tier. */
  readonly title: string;
  /** The commander title Premium+ gives at purchase. */
  readonly plusTitle: string;
}

export interface PassProduct {
  readonly price: OfferPrice;
  readonly name: string;
}

/** Past seasons' skins rotating into the Master's Collection (meta/seasonSkins.ts). */
export interface MastersRotation {
  /** The Master's Collection offer whose one skin prize a season skin replaces ("" for no rotation). */
  readonly offer: string;
  /** Full seasons that must pass after a skin's season ends before it rotates in. */
  readonly afterSeasons: number;
}

export interface SeasonsData {
  /** UTC day number of Season 1's first day (a Monday). */
  readonly epochDay: number;
  readonly daysPerSeason: number;
  /** The pass opens (shows, earns XP) once this level is cleared. */
  readonly unlockAfterLevel: number;
  readonly tiers: number;
  readonly xpPerTier: number;
  readonly xp: Readonly<Record<PassXpSource, number>>;
  /** A loss earns XP only after clearing this many stages. */
  readonly lossMinStages: number;
  readonly premium: PassProduct;
  readonly premiumPlus: PassProduct & { readonly tierSkip: number };
  /** In tier order (every 3rd tier). */
  readonly free: readonly PassTierReward[];
  /** One per tier, in tier order. */
  readonly premiumTrack: readonly PassTierReward[];
  readonly seasons: readonly SeasonTheme[];
  /** Gems the cosmetic tier pays instead of a season skin already owned (a later loop of the list); an owned season skin's refund. */
  readonly repeatSkinGems: number;
  readonly mastersRotation: MastersRotation;
}

/** No seasons data (an old bundle, a test fixture): the pass never opens. */
export function emptySeasons(): SeasonsData {
  const none: OfferPrice = { product: "", label: "$0.00", cents: 0 };
  return {
    epochDay: 0, daysPerSeason: 28, unlockAfterLevel: Number.MAX_SAFE_INTEGER, tiers: 0, xpPerTier: 1,
    xp: { win: 0, loss: 0, order: 0, eventNode: 0, dailyRoad: 0 }, lossMinStages: 0,
    premium: { price: none, name: "" }, premiumPlus: { price: none, name: "", tierSkip: 0 }, free: [], premiumTrack: [], seasons: [],
    repeatSkinGems: 0, mastersRotation: { offer: "", afterSeasons: 1 },
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Json = any;

function int(v: Json, where: string): number {
  if (typeof v === "number" && Number.isInteger(v)) return v;
  throw new TypeError(`${where}: expected integer, got ${JSON.stringify(v)}`);
}
function str(v: Json, where: string): string {
  if (typeof v !== "string") throw new TypeError(`${where}: expected string, got ${JSON.stringify(v)}`);
  return v;
}

function product(p: Json, where: string): PassProduct {
  const label = str(p?.price, `${where}.price`);
  return { price: { product: str(p?.product, `${where}.product`), label, cents: priceCents(label) }, name: str(p?.name, `${where}.name`) };
}

function track(list: Json, where: string): PassTierReward[] {
  if (!Array.isArray(list)) throw new TypeError(`${where}: expected a list`);
  return list.map((t: Json, i: number) => ({ tier: int(t?.tier, `${where}[${i}].tier`), reward: parseReward(t?.reward ?? {}), cosmetic: t?.cosmetic === true }));
}

export function parseSeasons(root: Json): SeasonsData {
  const s = root !== null && typeof root === "object" && "seasons" in root ? root.seasons : undefined;
  if (s === undefined) return emptySeasons();
  const xp = {} as Record<PassXpSource, number>;
  for (const k of PASS_XP_SOURCES) xp[k] = int(s.xp?.[k], `seasons.xp.${k}`);
  return {
    epochDay: int(s.epochDay, "seasons.epochDay"), daysPerSeason: int(s.daysPerSeason, "seasons.daysPerSeason"),
    unlockAfterLevel: int(s.unlockAfterLevel, "seasons.unlockAfterLevel"), tiers: int(s.tiers, "seasons.tiers"), xpPerTier: int(s.xpPerTier, "seasons.xpPerTier"),
    xp, lossMinStages: int(s.lossMinStages, "seasons.lossMinStages"),
    premium: product(s.premium, "seasons.premium"),
    premiumPlus: { ...product(s.premiumPlus, "seasons.premiumPlus"), tierSkip: int(s.premiumPlus?.tierSkip, "seasons.premiumPlus.tierSkip") },
    free: track(s.free, "seasons.free"), premiumTrack: track(s.premiumTrack, "seasons.premiumTrack"),
    seasons: (Array.isArray(s.seasons) ? s.seasons : []).map((e: Json, i: number) => ({
      name: str(e?.name, `seasons.seasons[${i}].name`), skin: str(e?.skin ?? "", `seasons.seasons[${i}].skin`),
      title: str(e?.title, `seasons.seasons[${i}].title`), plusTitle: str(e?.plusTitle, `seasons.seasons[${i}].plusTitle`),
    })),
    // (older bundles have neither: no repeat payout, no rotation)
    repeatSkinGems: s.repeatSkinGems === undefined ? 0 : int(s.repeatSkinGems, "seasons.repeatSkinGems"),
    mastersRotation: s.mastersRotation === undefined ? { offer: "", afterSeasons: 1 }
      : { offer: str(s.mastersRotation.offer, "seasons.mastersRotation.offer"), afterSeasons: int(s.mastersRotation.afterSeasons, "seasons.mastersRotation.afterSeasons") },
  };
}
